// workspaceService - Workspace API client
// Implements US-2.1: Create and List Workspaces
// Implements US-2.2: Join Workspace with Role Assignment
// Implements US-2.3: Rename Workspace
// Implements US-2.4: Close Workspace with Export Status Check
// Implements US-1.2: Viewer Guest Access (support for viewer tokens)

import axios from 'axios';
import apiClient from '../utils/apiClient';
import { getToken, getViewerToken } from '../utils/tokenStorage';
import {
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  ListWorkspacesResponse,
  GetWorkspaceResponse,
  JoinWorkspaceRequest,
  JoinWorkspaceResponse,
  RenameWorkspaceRequest,
  RenameWorkspaceResponse,
  CloseWorkspaceResponse,
} from '../types';

/**
 * GET authentication headers with JWT token
 * - Checks for user token first, then viewer token
 * - Returns headers object for axios requests
 *
 * @returns Authorization headers
 */
function getAuthHeaders() {
  // PRIORITIZE user token over viewer token
  const token = getToken() || getViewerToken();
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
}

/**
 * CREATE new workspace
 * - Requires authentication
 * - Validates name and framework selection
 * - Returns workspace with generated room code
 *
 * @param data Workspace creation data (name, frameworkSlug)
 * @returns Created workspace with room code
 * @throws Error if request fails
 */
export async function createWorkspace(data: CreateWorkspaceRequest): Promise<CreateWorkspaceResponse> {
  try {
    const response = await apiClient.post<CreateWorkspaceResponse>(
      '/api/workspaces',
      data,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to create workspace');
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * LIST user's workspaces
 * - Requires authentication
 * - Returns all workspaces created by user
 * - Sorted by last activity (most recent first)
 *
 * @returns List of user's workspaces
 * @throws Error if request fails
 */
export async function listWorkspaces(): Promise<ListWorkspacesResponse> {
  try {
    const response = await apiClient.get<ListWorkspacesResponse>(
      '/api/workspaces',
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to fetch workspaces');
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * GET workspace by ID
 * - Requires authentication
 * - Returns full workspace details with items, scores, notes
 * - Includes user role (user or viewer)
 *
 * @param workspaceId Workspace UUID
 * @returns Workspace details with role
 * @throws Error if request fails or workspace not found
 */
export async function getWorkspaceById(workspaceId: string): Promise<GetWorkspaceResponse> {
  try {
    const response = await apiClient.get<GetWorkspaceResponse>(
      `/api/workspaces/${workspaceId}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to fetch workspace');
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * JOIN workspace via room code
 * - Requires user authentication (viewers use /api/auth/viewer instead)
 * - Validates room code and checks workspace status
 * - Returns workspace ID and role for navigation
 *
 * @param data Join request data (room code)
 * @returns Workspace ID and role assignment
 * @throws Error if room code invalid, workspace closed, or request fails
 */
export async function joinWorkspace(data: JoinWorkspaceRequest): Promise<JoinWorkspaceResponse> {
  try {
    const response = await apiClient.post<JoinWorkspaceResponse>(
      '/api/workspaces/join',
      data,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to join workspace');
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * RENAME workspace
 * - Requires user authentication and workspace ownership
 * - Validates name (non-empty, max 200 chars)
 * - Broadcasts name change to all participants
 *
 * @param workspaceId Workspace UUID
 * @param data Rename request data (new name)
 * @returns Updated workspace
 * @throws Error if validation fails or request fails
 */
export async function renameWorkspace(
  workspaceId: string,
  data: RenameWorkspaceRequest
): Promise<RenameWorkspaceResponse> {
  try {
    const response = await apiClient.patch<RenameWorkspaceResponse>(
      `/api/workspaces/${workspaceId}`,
      data,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to rename workspace');
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * CLOSE workspace
 * - Requires user authentication and workspace ownership
 * - Sets workspace status to 'closed' and records closed_at timestamp
 * - Broadcasts closure event to all participants
 * - Closed workspaces become read-only (cannot be reopened in MVP)
 *
 * @param workspaceId Workspace UUID
 * @returns Closed workspace info
 * @throws Error if request fails
 */
export async function closeWorkspace(workspaceId: string): Promise<CloseWorkspaceResponse> {
  try {
    const response = await apiClient.post<CloseWorkspaceResponse>(
      `/api/workspaces/${workspaceId}/close`,
      {},
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to close workspace');
    }
    throw new Error('An unexpected error occurred');
  }
}
